import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo, useRef } from "react";

import { SearchQueryOrderDirection } from "types/generated";

import { SEARCH_SPACELIFT_VCS_REPO_COMMITS } from "./gql";

type UseSearchSpaceliftVcsRepoCommitsProps = {
  repoId: string;
  fullTextSearch?: string;
  skip?: boolean;
};

const PAGE_SIZE = 50;
const ORDER_BY = [{ field: "createdAt", direction: SearchQueryOrderDirection.Desc }];

const useSearchSpaceliftVcsRepoCommits = ({
  repoId,
  fullTextSearch,
  skip,
}: UseSearchSpaceliftVcsRepoCommitsProps) => {
  const fetchingRef = useRef(false);

  const { data, loading, error, previousData, fetchMore } = useQuery(
    SEARCH_SPACELIFT_VCS_REPO_COMMITS,
    {
      variables: {
        repoID: repoId,
        input: {
          after: null,
          first: PAGE_SIZE,
          fullTextSearch: fullTextSearch || null,
          orderBy: ORDER_BY,
          predicates: null,
          requestedPage: null,
        },
      },
      skip,
    }
  );

  const commits = useMemo(() => {
    const edges = data?.searchRepoCommits.edges || previousData?.searchRepoCommits.edges;
    return edges?.map((edge) => edge.node) || [];
  }, [data, previousData]);

  const pageInfo = data?.searchRepoCommits.pageInfo || previousData?.searchRepoCommits.pageInfo;
  const hasMore = !!pageInfo?.hasNextPage;

  const loadMore = useCallback(() => {
    if (fetchingRef.current || !hasMore) return;

    fetchingRef.current = true;

    fetchMore({
      variables: {
        input: {
          after: pageInfo?.endCursor,
          first: PAGE_SIZE,
          fullTextSearch: fullTextSearch || null,
          orderBy: ORDER_BY,
          predicates: null,
          requestedPage: null,
        },
      },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous;

        return {
          searchRepoCommits: {
            ...fetchMoreResult.searchRepoCommits,
            edges: [
              ...previous.searchRepoCommits.edges,
              ...fetchMoreResult.searchRepoCommits.edges,
            ],
          },
        };
      },
    }).finally(() => {
      fetchingRef.current = false;
    });
  }, [fetchMore, fullTextSearch, hasMore, pageInfo?.endCursor]);

  return {
    commits,
    loading,
    error,
    hasMore,
    loadMore,
  };
};

export default useSearchSpaceliftVcsRepoCommits;
